// LastCheckedPill v0.1.0 — centered freshness indicator. Renders a thin
// horizontal strip under the header showing how long ago the dashboard
// last refreshed its data (state.fetched_at). The 1s tick re-render
// cadence comes from the parent Home's `now` clock state, so this
// component only computes a string from props.
//
// Color ladder:
//   - fresh (< 90s):   cyan accent. Normal operating state.
//   - warm (90s-3m):   warn yellow. SSE may have dropped; HTTP poll
//                       fallback is doing the work.
//   - stale (> 3m):    bad red. Something is wrong with /state — the
//                       data on screen is unreliable.
//
// Why these thresholds: the api-worker /state route caches for 15s and
// the frontend HTTP poll runs every 60s; SSE pushes more frequently when
// active. ~90s is the upper bound on a healthy normal poll cadence
// (one missed beat). 3m means at least three missed cycles — at that
// point the user shouldn't trust the colored dots on screen.
import { timeAgo } from '../lib/formatTime.js';

interface Props {
  fetchedAt: string;
  now: Date;
}

export default function LastCheckedPill({ fetchedAt, now }: Props) {
  const ts = Date.parse(fetchedAt);
  const ageSec = Number.isNaN(ts) ? 0 : Math.max(0, Math.floor((now.getTime() - ts) / 1000));

  let pillCls = 'border-accent/50 bg-accent/10 text-accent';
  let dotCls = 'bg-accent';
  if (ageSec >= 180) {
    pillCls = 'border-bad/60 bg-bad/15 text-bad';
    dotCls = 'bg-bad animate-pulse';
  } else if (ageSec >= 90) {
    pillCls = 'border-warn/60 bg-warn/15 text-warn';
    dotCls = 'bg-warn';
  }

  const label = ageSec < 5 ? 'just now' : timeAgo(fetchedAt, now);

  // No wrapper — caller positions the pill (currently absolute-centered in
  // the page header, see Home.tsx). Lets us drop a dedicated strip below the
  // header that was costing ~32px of fold space.
  return (
    <span
      aria-live="polite"
      className={`inline-flex items-center gap-2 px-3 py-0.5 rounded-full border text-[10px] font-mono tracking-[2px] uppercase ${pillCls}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotCls}`} aria-hidden="true" />
      Last checked {label}
    </span>
  );
}
